/**
 * buildResponsivePicture
 *
 * Works with either:
 *  - (imgEl: HTMLImageElement)
 *  - (src: string, alt?: string, attrs?: Record&lt;string,string|number|boolean&gt;)
 *
 * It preserves editor-chosen size (S/M/L) by inferring from:
 *  - element.getAttribute('width')       // numeric width set by your Image extension
 *  - element.getAttribute('data-display')// "inline" | "block"
 * and falls back to sane defaults.
 */
export function buildResponsivePicture(arg1, altMaybe, extraAttrs = {}) {
  // ---- Normalize inputs to a single shape ----
  let imgEl = null;
  let rawSrc = null;
  let alt = '';

  if (typeof arg1 === 'string') {
    // Old call style: (src, alt?)
    rawSrc = arg1 || '';
    alt = typeof altMaybe === 'string' ? altMaybe : '';
  } else {
    // New call style: (imgEl)
    imgEl = arg1;
    if (!imgEl || !imgEl.getAttribute) return null;
    rawSrc = imgEl.getAttribute('src') || '';
    alt = imgEl.getAttribute('alt') || '';
  }

  if (!rawSrc || typeof rawSrc !== 'string') return null;

  // Pull sizing/display hints
  const dataDisplay =
    (imgEl && imgEl.getAttribute && imgEl.getAttribute('data-display')) || 'inline';

  // Respect explicit width when present (editor S/M/L)
  const widthAttr =
    (imgEl && imgEl.getAttribute && imgEl.getAttribute('width')) ||
    (extraAttrs && typeof extraAttrs.width !== 'undefined' ? String(extraAttrs.width) : null);

  const numericWidth = widthAttr ? parseInt(String(widthAttr), 10) : NaN;

  // Map the chosen width to our responsive base
  let baseW;
  if (!isNaN(numericWidth)) {
    if (numericWidth <= 480) baseW = 480;
    else if (numericWidth <= 768) baseW = 768;
    else if (numericWidth <= 1200) baseW = 1200;
    else baseW = 1600;
  } else {
    baseW = dataDisplay === 'block' ? 768 : 480;
  }

  // Build a bounded set of sizes up to the selected base
  const targetMax = Math.max(baseW, 480);
  const widths = Array.from(new Set([480, 768, 1200, 1600].filter((w) => w <= targetMax)));

  // Safe param appender (no crash on bad inputs)
  const withParams = (base, params) => {
    if (typeof base !== 'string' || !base) return '';
    const sep = base.indexOf('?') !== -1 ? '&' : '?';
    return `${base}${sep}${params}`;
  };

  // Build sources
  const webpSources = widths
    .map(
      (w) =>
        `<source type="image/webp" srcset="${withParams(rawSrc, `w=${w}`)}" media="(max-width: ${w}px)">`,
    )
    .join('');

  const jpegSources = widths
    .map(
      (w) =>
        `<source type="image/jpeg" srcset="${withParams(rawSrc, `w=${w}`)}" media="(max-width: ${w}px)">`,
    )
    .join('');

  // Final img (respect baseW; always keep it fluid)
  const safeAlt = String(alt ?? '').replace(/"/g, '&quot;');
  const imgHTML = `<img src="${withParams(rawSrc, `w=${baseW}`)}" alt="${safeAlt}" style="max-width:100%;height:auto;">`;

  const tpl = document.createElement('template');
  tpl.innerHTML = `<picture>${webpSources}${jpegSources}${imgHTML}</picture>`;
  const node = tpl.content.firstChild;
  return node && node.nodeType === 1 ? node : null;
}

export default buildResponsivePicture;
